import React from 'react';
import { Box, Button, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { GridView } from '@mui/icons-material';
import { ListingImage } from './ListingImage';
import { PropsWithLoadingState } from '../../../utilities';
import { useTranslation } from 'react-i18next';

const SPACING = 2;

export interface IListingPicturesProps {
    /** The list of image urls */
    images: string[];
    /** The total of images in the listing */
    totImages: number;
    /** The callback when pressed on the show more action */
    onShowMoreClick?: () => void;
}

const ListingPicturesMobile = ({ images, totImages, onShowMoreClick, loading }: PropsWithLoadingState<IListingPicturesProps>) => {
    return (
        <Box sx={{ position: 'relative', width: "100%", aspectRatio: 2 }} onClick={onShowMoreClick}>
            <ListingImage loading={loading} imageUrl={!loading ? images[0] : undefined} />
            {!loading && totImages > 1 &&
                <Box sx={{ position: 'absolute', top: 10, right: 10, padding: 1, borderRadius: 1, backgroundColor: "#FFFFFF88" }}>
                    <Typography>{`1/${totImages}`}</Typography>
                </Box>
            }
        </Box>
    );
}

const ListingPicturesWeb = ({ images, totImages, onShowMoreClick, loading }: PropsWithLoadingState<IListingPicturesProps>) => {
    const { t } = useTranslation();
    const MAX_NUM_PIC_SHOWN = 5;
    const hasShowMoreButton = !loading && (images.length > MAX_NUM_PIC_SHOWN || totImages > MAX_NUM_PIC_SHOWN) && !!onShowMoreClick
    return (
        <Box sx={{ position: 'relative', width: "100%", aspectRatio: 2.5 }}>
            <Grid container columnSpacing={SPACING} sx={{ height: '100%' }} onClick={!hasShowMoreButton ? onShowMoreClick : undefined}>
                <Grid item xs={6} maxHeight={'100%'}>
                    <ListingImage loading={loading} imageUrl={!loading ? images[0] : undefined} />
                </Grid>
                <Grid container item xs={6} columnSpacing={SPACING} rowGap={SPACING} maxHeight={'100%'}>
                    {
                        new Array(MAX_NUM_PIC_SHOWN - 1).fill('').map((_, index) =>
                            <Grid item xs={6} key={`Picture-${index}`} sx={{ aspectRatio: 2 }}>
                                <ListingImage loading={loading} imageUrl={!loading ? images[index + 1] : undefined} />
                            </Grid>)
                    }
                </Grid>
            </Grid>
            {
                hasShowMoreButton
                &&
                <Box sx={{ position: 'absolute', bottom: 10, right: 10 }}>
                    <Button variant={'contained'} onClick={onShowMoreClick} startIcon={<GridView />}>{t('listing.pictures.showAllBtn')}</Button>
                </Box>
            }
        </Box>
    )
}

/**
 * The Listing Picture on detail page
 * @param props IListingPicturesProps
 * @returns The rendering of the picture in the listing details
 */
export const ListingPictures = (props: PropsWithLoadingState<IListingPicturesProps>) => {
    const theme = useTheme();
    const smUp = useMediaQuery(theme.breakpoints.up('sm'));
    return smUp ?
        < ListingPicturesWeb {...props} />
        :
        <ListingPicturesMobile {...props} />
}
